import { OrganisationWithRole, UserRole } from '@avanzu/types';
import {
  AssignmentTwoTone,
  AutoFixHighTwoTone,
  BarChartTwoTone,
  ChevronLeft,
  ChevronRight,
  DevicesTwoTone,
  GroupTwoTone,
  PieChartTwoTone,
  PlaylistAddTwoTone,
  ScienceTwoTone,
  SettingsTwoTone,
} from '@mui/icons-material';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ProfileMenu } from '../components/ProfileMenu';
import ProjectSelectHeader from '../components/ProjectSelectHeader';
import UserAvatar from '../components/UserAvatar';
import { XeroSyncStatus } from '../components/XeroSyncStatus';
import { ChatGenerateStatus } from '../components/ChatGenerateStatus';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { useProject } from '../contexts/ProjectContext';
import AvanzuHeader from '../components/AvanzuHeader';
import VersionBadge from '../components/VersionBadge';
import { ActivitiesPageRoles } from '../pages/app/Activities';
import { AssetsPageRoles } from '../pages/app/Assets';
import { AccountsPageRoles } from '../pages/app/Accounts';
import { EmployeePageRoles } from '../pages/app/Employees';
import { TransactionPageRoles } from '../pages/app/Transactions';
import { ReportPageRoles } from '../pages/app/Reports';

const drawerWidth = 240;
const drawerWidthCollapsed = 64;

interface NavItem {
  text: string;
  path: string;
  icon: React.ComponentType;
  roles: UserRole[];
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const AllRoles: UserRole[] = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

const getNavSections = (currentProjectId?: string | null): NavSection[] => {
  return [
    // Base items (no section title)
    {
      items: [
        { text: 'Dashboard', path: '/app/', icon: BarChartTwoTone, roles: AllRoles },
        { text: 'Projects', path: '/app/projects', icon: AssignmentTwoTone, roles: AllRoles },
      ],
    },
    // Setup & Configuration
    {
      title: 'Setup & Configuration',
      items: [
        {
          text: 'Activities',
          path: currentProjectId ? `/app/activities/${currentProjectId}` : '/app/activities',
          icon: ScienceTwoTone,
          roles: ActivitiesPageRoles,
        },
        {
          text: 'Employees',
          path: currentProjectId ? `/app/employees/${currentProjectId}` : '/app/employees',
          icon: GroupTwoTone,
          roles: EmployeePageRoles,
        },
        {
          text: 'Account Rules',
          path: currentProjectId ? `/app/rules/${currentProjectId}` : '/app/rules',
          icon: AutoFixHighTwoTone,
          roles: AccountsPageRoles,
        },
        {
          text: 'Fixed Assets',
          path: currentProjectId ? `/app/assets/${currentProjectId}` : '/app/assets',
          icon: DevicesTwoTone,
          roles: AssetsPageRoles,
        },
      ],
    },
    // Data Entry
    {
      title: 'Data Entry',
      items: [
        {
          text: 'Transactions',
          path: currentProjectId ? `/app/transactions/${currentProjectId}` : '/app/transactions',
          icon: PlaylistAddTwoTone,
          roles: TransactionPageRoles,
        },
      ],
    },
    // Reports & Analysis
    {
      title: 'Reports & Analysis',
      items: [
        {
          text: 'Project Report',
          path: currentProjectId ? `/app/summary/${currentProjectId}` : '/app/summary',
          icon: PieChartTwoTone,
          roles: ReportPageRoles,
        },
        // {
        //   text: 'Transaction Analysis',
        //   path: currentProjectId ? `/app/reports/${currentProjectId}` : '/app/reports',
        //   icon: PieChartTwoTone,
        // },
        // {
        //   text: 'Depreciation',
        //   path: currentProjectId ? `/app/depreciation/${currentProjectId}` : '/app/depreciation',
        //   icon: ShowChartTwoTone,
        // },
        // {
        //   text: 'Salaries',
        //   path: currentProjectId ? `/app/salaries/${currentProjectId}` : '/app/salaries',
        //   icon: RequestQuoteTwoTone,
        // },
      ],
    },
    // Settings (no section title)
    {
      items: [{ text: 'Settings', path: '/settings', icon: SettingsTwoTone, roles: AllRoles }],
    },
  ];
};

export const AppWrapper: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganisation } = useAuth();
  const { currentProjectId } = useProject();
  const { notificationCount } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Collapse drawer by default on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const navSections = getNavSections(currentProjectId);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const isActive = (path: string) => {
    if (path === '/app/' && location.pathname === '/app/') return true;
    return path !== '/app/' && location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar sx={{ mr: 6 }}>
          <Box sx={{ ml: 2, width: '220px' }}>
            <AvanzuHeader />
          </Box>
          <ProjectSelectHeader />
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={2}>
            {!isMobile && <OrganisationHeader currentOrganisation={currentOrganisation} />}
            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Badge
                badgeContent={notificationCount}
                color="error"
                invisible={notificationCount === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: '18px',
                    minWidth: '18px',
                  },
                }}
              >
                <UserAvatar
                  initials={
                    (user?.firstName?.[0] || '')?.toUpperCase() +
                    (user?.lastName?.[0] || '')?.toUpperCase()
                  }
                  avatarUrl={user?.avatarUrl || undefined}
                  size="small"
                  color="primary.main"
                />
              </Badge>
            </IconButton>
          </Stack>
          <ProfileMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            user={user}
            userRole={currentOrganisation?.role as any}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Drawer
          sx={{
            width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              transition: 'width 0.3s',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Box sx={{ mt: 2, ml: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <List sx={{ flex: 1 }}>
              {navSections.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {/* Add divider before sections (except first) */}
                  {sectionIndex > 0 && <Divider sx={{ my: 1, mx: 1 }} />}

                  {/* Section title (only if expanded and title exists) */}
                  {/* {sidebarOpen && section.title && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      {section.title}
                    </Typography>
                  )} */}

                  {/* Section items */}
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    // Check if user has one of the required roles for this item
                    if (
                      !user ||
                      !currentOrganisation ||
                      !item.roles.includes(currentOrganisation.role as UserRole)
                    ) {
                      return null;
                    }

                    return (
                      <Tooltip
                        key={item.text}
                        title={!sidebarOpen ? item.text : ''}
                        placement="right"
                      >
                        <ListItemButton
                          selected={active}
                          onClick={() => navigate(item.path)}
                          sx={{
                            height: 36,
                            px: 0,
                            ml: 2,
                            mr: 0,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 42,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 2 : 1,
                            }}
                          >
                            <Icon />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary={item.text}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>

            {/* Status indicators at bottom */}
            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ mb: 1 }} />
              <ChatGenerateStatus sidebarOpen={sidebarOpen} />
              <XeroSyncStatus sidebarOpen={sidebarOpen} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: sidebarOpen ? 'flex-end' : 'center',
                mb: 2,
                ml: sidebarOpen ? 0 : 2,
              }}
            >
              <IconButton onClick={toggleSidebar} size="small">
                {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            pr: 10,
            overflow: 'auto',
          }}
        >
          <Outlet />
          <VersionBadge />
        </Box>
      </Box>
    </Box>
  );
};

const OrganisationHeader: React.FC<{ currentOrganisation: OrganisationWithRole | null }> = ({
  currentOrganisation,
}) => {
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {currentOrganisation?.name || 'No Organisation'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {currentOrganisation?.role}
      </Typography>{' '}
    </>
  );
};

export default AppWrapper;
